/**
 * Confirm the provider-neutral document outcome for one feed disconnect.
 *
 * Every connector uses the same detach-or-delete contract. Keeping the choice
 * here prevents provider action menus from inventing different retention rules.
 */
import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@/components/ui'
import { waitForVaultJob } from '@/subsystems/vault/services/vault-jobs'
import { disconnectConnectorFeed } from '../api'
import type { ConnectorDocumentDisposition, ConnectorFeed } from '../types'

export function DisconnectConnectorDialog({
  api,
  orgId,
  workspaceId,
  vaultId,
  feed,
  onOpenChange,
  onComplete,
}: {
  api: NodeApiClient
  orgId: string
  workspaceId: string
  vaultId: string
  feed?: Pick<ConnectorFeed, 'feedId' | 'remoteFolderName'>
  onOpenChange: (open: boolean) => void
  onComplete: () => void | Promise<unknown>
}) {
  const [disposition, setDisposition] = useState<ConnectorDocumentDisposition>()
  const disconnect = async (documentDisposition: ConnectorDocumentDisposition) => {
    if (!feed) return
    setDisposition(documentDisposition)
    try {
      const job = await disconnectConnectorFeed(
        api,
        orgId,
        vaultId,
        feed.feedId,
        documentDisposition,
      )
      await waitForVaultJob(api, orgId, workspaceId, job.jobId)
      await onComplete()
      toast.success(documentDisposition === 'detach'
        ? 'Connector disconnected; documents and analyses were kept'
        : 'Connector and its documents were deleted')
      onOpenChange(false)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? error?.message ?? 'Could not remove connector')
    } finally {
      setDisposition(undefined)
    }
  }

  return (
    <AlertDialog
      open={Boolean(feed)}
      onOpenChange={open => { if (!open && !disposition) onOpenChange(false) }}
    >
      <AlertDialogContent data-testid="connector-disconnect-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Disconnect {feed?.remoteFolderName}?</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <span className="block">
              This stops scheduled syncs and deletes this connector&apos;s settings,
              checkpoints, and unfinished estimate data.
            </span>
            <span className="block">
              Choose whether transferred documents stay as detached workspace documents
              with their completed analyses, or are permanently deleted with those analyses.
              A reconnected source will not adopt detached documents.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={Boolean(disposition)}>Cancel</AlertDialogCancel>
          <Button
            variant="outline"
            disabled={Boolean(disposition) || !feed}
            onClick={event => {
              event.preventDefault()
              void disconnect('detach')
            }}
          >
            {disposition === 'detach' ? 'Keeping documents…' : 'Keep documents'}
          </Button>
          <AlertDialogAction
            disabled={Boolean(disposition) || !feed}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={event => {
              event.preventDefault()
              void disconnect('delete')
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {disposition === 'delete' ? 'Deleting…' : 'Delete documents'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
